/**
 * Modal - 모달 대화상자 컴포넌트
 *
 * Portal로 렌더링되며, 포커스 트랩, ESC 닫기, 배경 클릭 닫기를 지원.
 * @param isOpen - 모달 표시 여부
 * @param onClose - 닫기 콜백
 * @param footer - 하단 버튼 영역에 렌더링할 요소
 */
'use client';

import React, { useEffect, useCallback, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import styles from './Modal.module.css';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Accessible label used when no visible title is rendered (aria-label on the dialog). */
  ariaLabel?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  icon?: React.ReactNode;
  description?: string;
  layout?: 'default' | 'stacked';
  closeDisabled?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  size = 'md',
  icon,
  description,
  layout = 'default',
  closeDisabled = false,
  initialFocusRef,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !closeDisabled) {
        onClose();
      }
    },
    [onClose, closeDisabled],
  );

  // Focus trap: keep focus within the modal
  const handleTabKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;

    const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length === 0) {
      e.preventDefault();
      dialogRef.current.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (
        document.activeElement === firstElement ||
        !Array.from(focusableElements).includes(document.activeElement as HTMLElement)
      ) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (
        document.activeElement === lastElement ||
        !Array.from(focusableElements).includes(document.activeElement as HTMLElement)
      ) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  // Focus the first focusable element only when the modal opens
  useEffect(() => {
    if (!isOpen) return;
    let frame = 0;
    const previousOverflow = document.body.style.overflow;
    if (isOpen) {
      // Save previously focused element
      previousFocusRef.current = document.activeElement as HTMLElement;

      document.body.style.overflow = 'hidden';

      frame = requestAnimationFrame(() => {
        if (dialogRef.current) {
          const firstFocusable = dialogRef.current.querySelector<HTMLElement>(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"]):not([disabled])',
          );
          const supplied = initialFocusRef?.current;
          if (
            supplied?.isConnected &&
            dialogRef.current.contains(supplied) &&
            !supplied.matches(':disabled')
          ) {
            supplied.focus();
          } else if (firstFocusable) {
            firstFocusable.focus();
          } else {
            dialogRef.current.focus();
          }
        }
      });
    }

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;

      // Restore focus to previously focused element
      if (previousFocusRef.current?.isConnected && !previousFocusRef.current.matches(':disabled')) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, initialFocusRef]);

  useEffect(() => {
    if (
      isOpen &&
      closeDisabled &&
      !dialogRef.current?.querySelector(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]',
      )
    ) {
      dialogRef.current?.focus();
    }
  }, [isOpen, closeDisabled]);

  // Keyboard event listeners (separate to avoid re-triggering focus)
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTabKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTabKeyDown);
    };
  }, [isOpen, handleEscape, handleTabKeyDown]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !closeDisabled) {
      onClose();
    }
  };

  const modal = (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className={`${styles.dialog} ${styles[size]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        {(title || icon) && (
          <div className={styles.header}>
            <div>
              {icon && <div className={styles.iconWrapper}>{icon}</div>}
              <h3 className={styles.title} id={titleId}>
                {title}
              </h3>
              {description && (
                <p className={styles.description} id={descriptionId}>
                  {description}
                </p>
              )}
            </div>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="닫기"
              disabled={closeDisabled}
            >
              <X size={20} />
            </button>
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer && (
          <div className={`${styles.footer} ${layout === 'stacked' ? styles.stackedFooter : ''}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modal, document.body);
  }

  return null;
}
